import React, { useEffect, useState } from 'react';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import Button from '@mui/material/Button';
import { Element, scroller } from 'react-scroll';
import ChatBubble from '../chat/ChatBubble';
import CircularProgress from '@mui/material/CircularProgress';
import { chatWithTds } from '../../providers/chatbotApi';
import { toast } from 'react-toastify';
import { useOktaAuth } from '@okta/okta-react';
import ChatInputWithDocument from '../inputs/ChatInputWithDocument';
import BradyLink from '../BradyLink';
import { IconButton, Link, Tooltip, Typography, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DetailPanelType } from '../../enum/DetailPanelTypeEnum';
import { MobileConstants } from '../../constants';

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '70vw',
  height: '80vh',
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

const styleMobile = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100vw',
  height: '100vh',
  bgcolor: 'background.paper',
  p: 4,
};

interface Message {
  isUser: boolean;
  text: string;
  minHeight: string;
}

interface MessageHistory {
  [key: string]: Message[];
}

interface ChatWithDocumentsProps {
  materialId: string;
  isInDistillery: boolean;
  useIdForButton?: boolean;
}

const ChatWithDocuments: React.FC<ChatWithDocumentsProps> = ({ materialId, isInDistillery, useIdForButton }) => {
  const [open, setOpen] = useState(false);
  const isSmallScreen = useMediaQuery(MobileConstants.MOBILE_WIDTH_BREAKPOINT);
  const handleOpen = () => {
    setMessages(messageHistory && messageHistory[materialId] ? messageHistory[materialId] : []);
    setOpen(true);
  };
  const handleOpenStopPropagation = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.stopPropagation();
    setMessages(messageHistory && messageHistory[materialId] ? messageHistory[materialId] : []);
    setOpen(true);
  };
  const handleClose = () => {
    if (messageHistory) {
      messageHistory[materialId] = messages;
      setMessageHistory(messageHistory);
    }
    setOpen(false);
  };

  const [messages, setMessages] = useState<Message[]>([]);
  const [messageHistory, setMessageHistory] = useState<MessageHistory>({});
  const [isLoading, setIsLoading] = useState(false);
  const [updateHeights, setUpdateHeights] = useState(false);

  const { authState } = useOktaAuth();

  const handleNewMessage = async (message: string) => {
    let updatedMessages = [...messages, { text: message, isUser: true, minHeight: 'auto' } as Message];

    setMessages(updatedMessages);
    setIsLoading(true);

    const accessToken = authState?.accessToken?.accessToken;
    if (accessToken) {
      try {
        const apiResponse = await chatWithTds(accessToken, materialId, isInDistillery, message);
        updatedMessages = [
          ...updatedMessages,
          {
            text: apiResponse,
            isUser: false,
            minHeight: 'auto',
          },
        ];
      } catch (error) {
        console.error(error);
        toast.error('An error occurred while fetching the response from the bot.');
      }
    }

    setMessages(updatedMessages);
    setIsLoading(false);
  };

  const updateMessageHeights = () => {
    const updatedMessages = messages.map((message, index) => {
      if (index === messages.length - 2 && !message.isUser) {
        message.minHeight = '95vh';
      } else {
        message.minHeight = 'auto';
      }
      return message;
    });

    setMessages(updatedMessages);
  };

  useEffect(() => {
    if (messages.length > 0) {
      scroller.scrollTo('last-user-message-in-chat-with-tds', {
        duration: 500,
        delay: 100,
        smooth: true,
        containerId: 'chatbox-with-tds',
      });
    }

    setUpdateHeights(!updateHeights);
  }, [messages]);

  useEffect(() => {
    if (updateHeights) {
      updateMessageHeights();
    }
  }, [updateHeights]);

  return (
    <div>
      {materialId &&
        (useIdForButton ? (
          <Link onClick={handleOpenStopPropagation} style={{ cursor: 'pointer' }}>
            <Tooltip title={'Chat with TDS'}>
              <div>
                {materialId}
              </div>
            </Tooltip>
          </Link>
        ) : (
          <Button onClick={handleOpen}>Chat with the TDS Document</Button>
        ))}
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={open}
        onClose={handleClose}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <Fade in={open}>
          <Box sx={isSmallScreen ? styleMobile : style}>
            <IconButton
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: '25px',
              }}
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h4" sx={{ textAlign: 'center' }}>
              Chat with the TDS Document for
              <BradyLink type={DetailPanelType.Material} id={materialId} onlyShowId bNumber={materialId} />
            </Typography>
            <Box className="flex-column" sx={{ flexGrow: 1, p: 3, marginRight: '0px', minWidth: 0 }}>
              <Box className="flex-column" sx={{ flexGrow: 1, height: '100%' }}>
                {messages.length === 0 && <Box sx={{ height: '50vh' }} />}
                {messages.length > 0 && (
                  <Box
                    id="chatbox-with-tds"
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      mt: 2,
                      mb: 2,
                      flexGrow: 1,
                      height: '50vh',
                      overflowY: 'auto',
                    }}
                  >
                    {messages.map((message, index) => (
                      <Box key={index} sx={{ width: '100%', minHeight: message.minHeight }}>
                        <Element
                          name={
                            message.isUser && (index === messages.length - 1 || index === messages.length - 2)
                              ? 'last-user-message-in-chat-with-tds'
                              : 'other-message'
                          }
                        >
                          <ChatBubble message={message.text} isUser={message.isUser} backgroundColor="#FFFFFF" />
                        </Element>
                      </Box>
                    ))}
                    <Box sx={{ marginBottom: '70vh' }}>
                      {isLoading && (
                        <ChatBubble message={<CircularProgress />} isUser={false} backgroundColor="#FFFFFF" />
                      )}
                    </Box>
                  </Box>
                )}
                <ChatInputWithDocument onSendMessage={handleNewMessage} isInDistillery={isInDistillery} isLoading={isLoading} />
              </Box>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </div>
  );
};

export default ChatWithDocuments;
